import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/EmployeeList.css';

const EmployeeList = ({ onLogout }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    title: '',
    company: '',
    address: '',
    website: '',
    linkedin: '',
    status: 'active'
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees');
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      setError('Error fetching employees: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = isEditing ? `/api/employees/${formData.id}` : '/api/employees';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to save employee');

      // Reset form and refresh list
      resetForm();
      fetchEmployees();
    } catch (err) {
      setError('Error saving employee: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      email: '',
      phone: '',
      title: '',
      company: '',
      address: '',
      website: '',
      linkedin: '',
      status: 'active'
    });
    setIsEditing(false);
  };

  const handleEdit = (employee) => {
    setFormData({ ...employee });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const response = await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete employee');

      fetchEmployees();
    } catch (err) {
      setError('Error deleting employee: ' + err.message);
    }
  };

  console.log('Rendering EmployeeList page');

  if (loading) return <div>Loading...</div>;

  try {
    return (
      <div className="dashboard-layout">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="Pricol Logo" />
          </div>
          <nav className="sidebar-nav">
            <Link to="/dashboard" className="sidebar-link">Dashboard</Link>
            {/* <Link to="/users" className="sidebar-link">Users</Link> */}
            <Link to="/employees" className="sidebar-link">Employees</Link>
          </nav>
          <button className="sidebar-logout" onClick={() => onLogout(navigate)}>Logout</button>
        </aside>
        <main className="dashboard-main">
          <div className="employee-list-header">
            <h2>Employee Management</h2>
            <button className="employee-add-btn" onClick={resetForm}>{isEditing ? 'Cancel' : 'Add New Employee'}</button>
          </div>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleSubmit} className="employee-form-modern">
            <div className="employee-form-row">
              <div className="employee-form-group">
                <label>Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>Job Title</label>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required />
              </div>
            </div>
            <div className="employee-form-row">
              <div className="employee-form-group">
                <label>Company</label>
                <input type="text" name="company" value={formData.company} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>Website</label>
                <input type="text" name="website" value={formData.website} onChange={handleInputChange} required />
              </div>
              <div className="employee-form-group">
                <label>LinkedIn</label>
                <input type="text" name="linkedin" value={formData.linkedin || ''} onChange={handleInputChange} />
              </div>
              <div className="employee-form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="employee-form-actions">
              <button type="submit" className="employee-save-btn">{isEditing ? 'Update' : 'Add'} Employee</button>
            </div>
          </form>
          <div className="employee-table-container-modern">
            <table className="employee-table-modern">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.name}</td>
                    <td>{employee.title}</td>
                    <td>{employee.email}</td>
                    <td>{employee.phone}</td>
                    <td>
                      <span className={`employee-status-badge ${employee.status}`}>{employee.status === 'active' ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(employee)} className="employee-action-btn edit">Edit</button>
                      <Link to={`/profile/${employee.email}`} className="employee-action-btn view">View Card</Link>
                      <button onClick={() => handleDelete(employee.id)} className="employee-action-btn delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    );
  } catch (err) {
    console.error('EmployeeList render error:', err);
    return <div>Error rendering EmployeeList: {err.message}</div>;
  }
};

export default EmployeeList;